import type { ReactNode } from 'react'
import { useBottomSheet } from '../../../../../../../state-management/contexts/BottomSheetContext'
import {
  LumpsumAllocationImportantNoteStaticType,
  type LumpsumAllocationImportantNoteFund,
  type LumpsumAllocationImportantNoteRuleBased,
  type LumpsumAllocationImportantNotes
} from '../../api/allocationRecommendation.types'
import { PlanImportantNoteCard } from '../../shared/notes/PlanImportantNoteCard'
import { buildFactBasedImportantNotePoints } from '../../shared/notes/PlanImportantNoteDynamicInfoBottomSheet'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansLumpsumSheetEventIds, PlansScreen } from '../../../../analytics/plansEvents'
import {
  DirectFundsInsteadOfRegularInfoBottomSheet,
  GrowthPlanInsteadOfIdcwInfoBottomSheet,
  RankOneNotRecommendedInfoBottomSheet
} from '../../shared/notes/PlanImportantNoteStaticInfoBottomSheets'

type AllocationPlanImportantNoteProps = {
  importantNotes?: LumpsumAllocationImportantNotes | null
}

export function AllocationPlanImportantNote({ importantNotes }: AllocationPlanImportantNoteProps) {
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const notePoints = [
    ...buildRuleBasedImportantNotePoints(
      importantNotes?.ruleBased,
      openNestedBottomSheet,
      closeNestedBottomSheet
    ),
    ...buildFactBasedImportantNotePoints(
      importantNotes?.factBasedCuratedNotes,
      openNestedBottomSheet,
      closeNestedBottomSheet
    )
  ]

  if (notePoints.length === 0) {
    return null
  }

  return <PlanImportantNoteCard notePoints={notePoints} />
}

const linkClassName =
  'w-fit cursor-pointer text-[13px] font-[400] leading-[140%] text-[#1470CC] underline underline-offset-2'
const textClassName = 'text-[13px] font-[400] leading-[150%] text-[#3D3D3D]'

export function formatImportantNoteFundNames(
  funds: LumpsumAllocationImportantNoteFund[] | null | undefined
): string {
  const fundNames = (funds ?? [])
    .map((fund) => fund?.fundName?.trim() ?? '')
    .filter((fundName) => fundName.length > 0)

  if (fundNames.length === 0) {
    return ''
  }
  if (fundNames.length === 1) {
    return fundNames[0]
  }
  if (fundNames.length === 2) {
    return `${fundNames[0]} & ${fundNames[1]}`
  }
  return `${fundNames.slice(0, -1).join(', ')} & ${fundNames[fundNames.length - 1]}`
}

function buildSellFundsPhrase(funds: LumpsumAllocationImportantNoteFund[] | null | undefined): string {
  const fundNamesLabel = formatImportantNoteFundNames(funds)
  if (fundNamesLabel.length > 0) {
    return `for ${fundNamesLabel}`
  }
  return 'for these funds'
}

export function buildRuleBasedImportantNotePoints(
  ruleBasedNotes: LumpsumAllocationImportantNoteRuleBased[] | null | undefined,
  openNestedBottomSheet: (content: ReactNode) => void,
  closeNestedBottomSheet: () => void
): ReactNode[] {
  const points: ReactNode[] = []

  for (const ruleBasedNote of ruleBasedNotes ?? []) {
    const noteType = ruleBasedNote?.type
    const funds = ruleBasedNote?.funds
    const sellFundsPhrase = buildSellFundsPhrase(funds)

    if (noteType === LumpsumAllocationImportantNoteStaticType.HIGH_EXPOSURE_SWITCH) {
      points.push(
        <div className={textClassName}>
          {`The sell recommendation ${sellFundsPhrase} aims to reduce high exposure in a single fund, keeping your portfolio well-diversified.`}
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.SWP_TRANSFER) {
      points.push(
        <div className={textClassName}>
          {`The sell recommendation ${sellFundsPhrase} is via instalments or SWP. When moving large amounts, SWP reduces the risk of wrong market timing.`}
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.SELL_FUND_IDCW) {
      points.push(
        <div>
          <div className={textClassName}>
            For our buy recommendations, we only advise investing in Growth Plans instead of IDCW.
          </div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumAllocationRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <GrowthPlanInsteadOfIdcwInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Learn more
          </div>
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.SELL_FUND_REGULAR) {
      points.push(
        <div>
          <div className={textClassName}>
            For our buy recommendations, we only advise investing in Direct Funds instead of Regular
            Funds.
          </div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumAllocationRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <DirectFundsInsteadOfRegularInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Learn more
          </div>
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.BUY_FUND_NON_RANK_ONE) {
      points.push(
        <div>
          <div className={textClassName}>Why is Rank 1 not always recommended to buy?</div>
          <div
            className={linkClassName}
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  ScreenName: PlansScreen.LumpsumAllocationRecommendation,
                  eventId: PlansLumpsumSheetEventIds.ImportantNoteLearnMore,
                  buttonLabel: 'Learn more'
                }
              })
              openNestedBottomSheet(
                <RankOneNotRecommendedInfoBottomSheet onDismiss={closeNestedBottomSheet} />
              )
            }}
          >
            Learn more
          </div>
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.SELL_FUND_STCG) {
      points.push(
        <div className={textClassName}>
          {`We have recommended partial sell ${sellFundsPhrase} to optimise for STCG and Exit Load impact. For remaining units, we will provide advisory in the upcoming plans.`}
        </div>
      )
      continue
    }

    if (noteType === LumpsumAllocationImportantNoteStaticType.SELL_FUND_ELSS) {
      points.push(
        <div className={textClassName}>
          {`We have recommended partial sell ${sellFundsPhrase} due to lock-in units. For remaining units, we will provide advisory in the upcoming plans.`}
        </div>
      )
    }
  }

  return points
}
